import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { KeyboardKey } from '../../enums/keyboardKey'
import { NumberFormat } from '../../formatters/utils/NumberFormat'
import { LocaleIdentifier } from '../../types/localeIdentifier'
import { getEvaluatedExpression, getUnevaluatedExpression, isExpression } from '../../utils/numbers'
import { Input, InputProps, InputRef } from '../Input'
import { StepNavigation } from './elements/StepNavigation'
import { InputNumberValue } from './types/inputNumberValue'
import { StepDirection } from './types/stepDirection'
import { getFormattedValue } from './utils/getFormattedValue'
import { isEmptyValue } from './utils/isEmptyValue'
import { sanitizeMathCharacters } from './utils/sanitizeMathCharacters'

export interface InputNumberProps extends Omit<InputProps, 'value' | 'defaultValue' | 'onChange'> {
  defaultValue?: number
  isFallbackToZero?: boolean // when true, the input will fallback to '0' when there's no value
  keyboard?: boolean
  locale: LocaleIdentifier
  max?: number
  min?: number
  onChange?: (value: number) => void
  onClear?: () => void // called instead of onChange(0) when the input is emptied and isFallbackToZero is false
  onStep?: (value: number, step: number, direction: StepDirection) => void
  step?: number
  value?: number
  withCalculations?: boolean
  withFormatting?: boolean
}

export const InputNumber = forwardRef<InputRef, InputNumberProps>(
  (
    {
      alignment = 'right',
      defaultValue,
      disabled,
      isFallbackToZero = true,
      keyboard = false,
      locale,
      max,
      min,
      onBlur,
      onChange,
      onClear,
      onFocus,
      onPressEnter,
      onStep,
      placeholder,
      readOnly,
      step = 1,
      suffix,
      value,
      withCalculations = true,
      withFormatting = true,
      ...rest
    }: InputNumberProps,
    forwardedRef: Ref<InputRef>,
  ) => {
    const { value: valueFormatted } = getFormattedValue({
      value: defaultValue ?? value,
      isDisplayValueFormat: withFormatting,
      max,
      min,
      isFallbackToZero,
      locale,
    })

    const [currentValue, setCurrentValue] = useState<InputNumberValue>(valueFormatted)
    const inputRef = useRef<InputRef>(null)

    // Functions

    const submit = useCallback(
      (value?: InputNumberValue) => {
        const inputValue = !isEmptyValue(value) ? value : currentValue
        const shouldEvaluateExpression = !isEmptyValue(inputValue) || isFallbackToZero

        let evaluatedExpression // should remain undefined if shouldEvaluateExpression is false

        if (shouldEvaluateExpression) {
          evaluatedExpression = withCalculations
            ? getEvaluatedExpression(sanitizeMathCharacters(inputValue))
            : getUnevaluatedExpression(sanitizeMathCharacters(inputValue), locale)
        }

        const { value: valueFormatted } = getFormattedValue({
          value: evaluatedExpression,
          isDisplayValueFormat: withFormatting,
          max,
          min,
          isFallbackToZero,
          locale,
        })

        // not passing locale, as expressions are passed in default string en format
        const amountNumber = NumberFormat.parseToNumber(String(evaluatedExpression))

        setCurrentValue(valueFormatted)

        if (evaluatedExpression !== undefined) {
          onChange?.(amountNumber)
        }
      },
      [withCalculations, currentValue, withFormatting, max, min, isFallbackToZero, locale, onChange],
    )

    const handleInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const value = event?.currentTarget.value
        const valueNumber = isExpression(value) ? undefined : NumberFormat.parseToNumber(value, locale)

        setCurrentValue(value)

        if (onClear && !isFallbackToZero && isEmptyValue(value)) {
          onClear()
          return
        }

        if (valueNumber !== undefined) {
          onChange?.(valueNumber)
        }
      },
      [isFallbackToZero, locale, onChange, onClear],
    )

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        submit()
        onBlur?.(event)
      },
      [onBlur, submit],
    )

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        const currentValueNumber = NumberFormat.parseToNumber(String(currentValue), locale)

        // Clear the input when value is 0, so it's easier to start typing new value
        if (!currentValueNumber && isFallbackToZero && !readOnly) {
          setCurrentValue('')
        }

        onFocus?.(event)
      },
      [currentValue, isFallbackToZero, locale, onFocus, readOnly],
    )

    const handlePressEnter = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        submit()
        onPressEnter?.(event)
      },
      [onPressEnter, submit],
    )

    const handleStepChange = useCallback(
      (direction: StepDirection) => {
        const currentValueNumber = NumberFormat.parseToNumber(String(currentValue), locale)
        const directionMultiplier = direction === 'up' ? 1 : -1
        const currentValueNumberWithStep = currentValueNumber + step * directionMultiplier

        submit(currentValueNumberWithStep)
        onStep?.(currentValueNumberWithStep, step, direction)
      },
      [currentValue, locale, step, submit, onStep],
    )

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (!keyboard) {
          return
        }

        if (event.key === KeyboardKey.ArrowUp) {
          event.preventDefault()
          handleStepChange('up')
        } else if (event.key === KeyboardKey.ArrowDown) {
          event.preventDefault()
          handleStepChange('down')
        }
      },
      [handleStepChange, keyboard],
    )

    // Mutations

    useEffect(() => {
      if (value !== undefined) {
        const { value: valueFormatted, hasValueBeenLimited } = getFormattedValue({
          value,
          isDisplayValueFormat: withFormatting,
          max,
          min,
          isFallbackToZero,
          locale,
        })
        const { value: currentValueFormatted } = getFormattedValue({
          value: currentValue,
          isDisplayValueFormat: withFormatting,
          max,
          min,
          isFallbackToZero,
          locale,
        })
        const valueLimited = NumberFormat.parseToNumber(String(valueFormatted), locale)

        if (currentValueFormatted !== valueFormatted) {
          setCurrentValue(valueFormatted)
        }

        // code below updates value when props "min" or "max" are in use
        if (valueLimited !== undefined && hasValueBeenLimited) {
          onChange?.(valueLimited)
        }
      }
      // should trigger only on value change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    useImperativeHandle(forwardedRef, () => inputRef?.current as InputRef)

    return (
      <Input
        {...rest}
        alignment={alignment}
        autoComplete="off"
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onPressEnter={handlePressEnter}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={inputRef}
        suffix={
          suffix ||
          (keyboard && (
            <>
              <StepNavigation onChange={handleStepChange} />
            </>
          ))
        }
        value={currentValue}
      />
    )
  },
)
